/**
 * Expert-level template synchronization status component.
 * Shows sync status and allows manual sync.
 */

import React from 'react';
import { useTemplateSync } from '../hooks/useTemplateSync';
import { motion } from 'framer-motion';

interface TemplateSyncStatusProps {
  className?: string;
}

export const TemplateSyncStatus: React.FC<TemplateSyncStatusProps> = ({ 
  className = "" 
}) => {
  const { isSyncing, syncResult, error, syncTemplates, lastSyncTime } = useTemplateSync();

  const formatLastSync = (date: Date | null) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className={`template-sync-status ${className}`}>
      {/* Sync Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-md p-4 border border-gray-200"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Template Synchronization
          </h3>
          
          <button
            onClick={syncTemplates}
            disabled={isSyncing}
            className={`
              px-4 py-2 rounded-md font-medium transition-colors
              ${isSyncing 
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }
            `}
          >
            {isSyncing ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Syncing...
              </div>
            ) : (
              'Sync Templates'
            )}
          </button>
        </div>

        {/* Status Information */}
        <div className="space-y-3">
          {/* Last Sync Time */}
          <div className="flex items-center text-sm text-gray-600">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            <span>Last sync: {formatLastSync(lastSyncTime)}</span>
          </div>

          {/* Sync Result */}
          {syncResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-green-50 border border-green-200 rounded-md p-3"
            >
              <div className="flex items-center mb-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium text-green-800">
                  Sync Completed Successfully
                </span>
              </div>
              
              <div className="text-sm text-green-700">
                <p>Synced: {syncResult?.synced_count || 0} / {syncResult?.total_templates || 0} templates</p>
                
                {syncResult.errors && syncResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium">Warnings:</p>
                    <ul className="list-disc list-inside ml-2">
                      {syncResult.errors?.map((error, index) => (
                        <li key={index} className="text-xs">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Error Display */}
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-red-50 border border-red-200 rounded-md p-3"
            >
              <div className="flex items-center mb-2">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium text-red-800">
                  Sync Failed
                </span>
              </div>
              
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}

          {/* Sync Progress */}
          {isSyncing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-blue-50 border border-blue-200 rounded-md p-3"
            >
              <div className="flex items-center mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm font-medium text-blue-800">
                  Synchronizing Templates...
                </span>
              </div>
              
              <div className="w-full bg-blue-200 rounded-full h-2">
                <motion.div
                  className="bg-blue-600 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 2, ease: 'easeInOut' }}
                />
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default TemplateSyncStatus;
